import { ACCOUNT_NETWORK_ALL_FIELDS } from '../constants/accountNetworkRequestFields'
import type { IpAddressAllocation } from '../types/cluster'

/**
 * Third AZ (AZ C) for IP allocations applies only to Production.
 * Sandbox, Non-Production, and similar types use two AZs only.
 */
export function accountRecordShowsIpAllocationAzC(accountType: string | undefined): boolean {
	const n = String(accountType ?? '')
		.trim()
		.toLowerCase()
	return n === 'production' || n === 'prod'
}

/** Singapore region defaults when the request did not specify zones. */
export const DEFAULT_VPC_AVAILABILITY_ZONES_NON_PROD =
	'ap-southeast-1a, ap-southeast-1b'

export const DEFAULT_VPC_AVAILABILITY_ZONES_PRODUCTION =
	'ap-southeast-1a, ap-southeast-1b, ap-southeast-1c'

/** Non-prod / sandbox: two AZs; Production: three AZs. */
export function defaultVpcAvailabilityZonesForAccountType(
	accountType: string | undefined
): string {
	return accountRecordShowsIpAllocationAzC(accountType)
		? DEFAULT_VPC_AVAILABILITY_ZONES_PRODUCTION
		: DEFAULT_VPC_AVAILABILITY_ZONES_NON_PROD
}

/** Wizard account-type multiselect: show AZ C only when Production is among selected types. */
export function wizardAccountTypesShowIpAllocationAzC(
	accountTypes: Array<{ value?: string }> | undefined
): boolean {
	return accountTypes?.some((t) => (t?.value ?? '') === 'Production') ?? false
}

/** Split the stored comma-separated AZ list into trimmed, non-empty entries. */
export function splitVpcAvailabilityZones(value: unknown): string[] {
	if (Array.isArray(value)) {
		return value.map((v) => String(v).trim()).filter(Boolean)
	}
	return String(value ?? '')
		.split(',')
		.map((v) => v.trim())
		.filter(Boolean)
}

/**
 * The AZ list is the single source of truth for whether AZ C is in play: three or more
 * zones means allocations carry a `cidrAzC`. Falls back to the account-type default while
 * the field is still blank (first render, before prefill lands).
 */
export function vpcAvailabilityZonesShowAzC(
	value: unknown,
	accountType?: string
): boolean {
	const zones = splitVpcAvailabilityZones(value)
	if (zones.length === 0) return accountRecordShowsIpAllocationAzC(accountType)
	return zones.length >= 3
}

/** Third zone appended/removed on toggle; existing first two entries are preserved. */
export function withThirdAvailabilityZone(value: unknown, enabled: boolean): string {
	const zones = splitVpcAvailabilityZones(value)
	const base = zones.length ? zones : splitVpcAvailabilityZones(
		DEFAULT_VPC_AVAILABILITY_ZONES_NON_PROD
	)
	if (!enabled) return base.slice(0, 2).join(', ')
	if (base.length >= 3) return base.join(', ')
	const third =
		splitVpcAvailabilityZones(DEFAULT_VPC_AVAILABILITY_ZONES_PRODUCTION)[2]
	return [...base.slice(0, 2), third].join(', ')
}

function formatScalar(value: unknown): string {
	if (value === undefined || value === null) return '—'
	if (typeof value === 'boolean') return value ? 'Yes' : 'No'
	const s = String(value).trim()
	if (!s) return '—'
	const lower = s.toLowerCase()
	if (lower === 'true') return 'Yes'
	if (lower === 'false') return 'No'
	return s
}

/** Fields where comma-separated values render one per line for readability */
const NETWORK_MULTILINE_COMMA_KEYS = new Set([
	'privateSubnetsCidr',
	'tgwSubnetsCidr',
	'secondaryCidrBlock',
	'publicCidrBlock',
	'vpcAvailabilityZones'
])

/**
 * Formatted string for network summary UI (supports newlines where helpful).
 */
export function formatAccountNetworkFieldValue(key: string, value: unknown): string {
	if (Array.isArray(value)) {
		const parts = value.map((v) => String(v).trim()).filter(Boolean)
		if (parts.length === 0) return '—'
		if (NETWORK_MULTILINE_COMMA_KEYS.has(key) && parts.length > 1) {
			return parts.join('\n')
		}
		return parts.join(', ')
	}

	const base = formatScalar(value)
	if (base === '—') return base

	if (
		NETWORK_MULTILINE_COMMA_KEYS.has(key) &&
		typeof value === 'string' &&
		value.includes(',')
	) {
		const parts = value.split(',').map((s) => s.trim()).filter(Boolean)
		if (parts.length > 1) {
			return parts.join('\n')
		}
	}

	return base
}

/** True if a network field value should be treated as present (non-empty strings / meaningful scalars). */
export function networkDetailFieldHasValue(key: string, value: unknown): boolean {
	if (value === undefined || value === null) return false
	if (typeof value === 'boolean') return true
	if (typeof value === 'string') return value.trim().length > 0
	return true
}

function fieldHasValue(key: string, value: unknown): boolean {
	return networkDetailFieldHasValue(key, value)
}

/** Copy only account network keys that have non-empty stored values. */
export function pickAccountNetworkFieldsFromDetails(
	details: Record<string, unknown> | undefined
): Record<string, unknown> {
	if (!details) return {}
	const out: Record<string, unknown> = {}
	for (const { key } of ACCOUNT_NETWORK_ALL_FIELDS) {
		if (networkDetailFieldHasValue(key, details[key])) {
			out[key] = details[key]
		}
	}
	return out
}

/** True if the request payload includes any network / secondary CIDR / IP allocation data. */
export function accountHasNetworkRequestData(
	details: Record<string, unknown> | undefined
): boolean {
	if (!details) return false
	for (const { key } of ACCOUNT_NETWORK_ALL_FIELDS) {
		if (fieldHasValue(key, details[key])) return true
	}
	const allocs = details.ip_address_allocations
	return Array.isArray(allocs) && allocs.length > 0
}

type KeyValueCell = { label: string; value: string }

/** Rows for @aws-northstar/ui KeyValuePairs (each inner array is one visual row). */
export function buildAccountNetworkKeyValuePairRows(
	details: Record<string, unknown> | undefined
): KeyValueCell[][] {
	if (!details) return []
	const cells: KeyValueCell[] = ACCOUNT_NETWORK_ALL_FIELDS.map(({ key, label }) => ({
		label,
		value: formatAccountNetworkFieldValue(key, details[key])
	}))
	const rows: KeyValueCell[][] = []
	for (let i = 0; i < cells.length; i += 2) {
		rows.push(cells.slice(i, i + 2))
	}
	return rows
}

export function formatIpAllocationsForTable(allocations: unknown): IpAddressAllocation[] {
	if (!Array.isArray(allocations)) return []
	return allocations.filter(
		(row): row is IpAddressAllocation =>
			row &&
			typeof row === 'object' &&
			'label' in row &&
			typeof (row as IpAddressAllocation).label === 'string'
	) as IpAddressAllocation[]
}
